/**
 * RectangularPrism Component (SVG)
 *
 * Renders a 3D rectangular prism using accurate SVG isometric projection.
 * Similar to Cube2D but with variable length, width, and height.
 */

import React from 'react';
import { Box } from '@mui/material';

interface RectangularPrismProps {
  length?: number;
  width?: number;
  height?: number;
  unit?: string;
  color?: string;
  strokeColor?: string;
}

export const RectangularPrism: React.FC<RectangularPrismProps> = ({
  length = 10,
  width = 6,
  height = 4,
  unit = 'cm',
  color = '#e0e7ff',
  strokeColor = '#9c27b0'
}) => {
  // Center the prism in the viewBox
  const centerX = 50;
  const centerY = 50;
  
  // Normalized sizes for visual consistency
  const maxDim = Math.max(length, width, height);
  const scale = 35 / maxDim; // Base scale factor to fit well in viewBox
  
  const L = length * scale;
  const W = width * scale;
  const H = height * scale;
  
  // Isometric projection math
  const cos30 = 0.8660254;
  const sin30 = 0.5;
  
  const project = (x: number, y: number, z: number) => ({
    x: centerX + (x - y) * cos30,
    y: centerY + (x + y) * sin30 - z
  });
  
  // Calculate vertices: x=length(right), y=width(left), z=height(up)
  const p000 = project(0, 0, 0); // Back-bottom
  const px00 = project(L, 0, 0); // Right-bottom
  const p0y0 = project(0, W, 0); // Left-bottom
  const pxy0 = project(L, W, 0); // Front-bottom
  const p00z = project(0, 0, H); // Back-top
  const px0z = project(L, 0, H); // Right-top
  const p0yz = project(0, W, H); // Left-top
  const pxyz = project(L, W, H); // Front-top
  
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="xMidYMid meet"
        style={{ width: '100%', maxWidth: '320px', height: '320px', display: 'block' }}
      >
        {/* Hidden edges (dashed) */}
        <line x1={p000.x} y1={p000.y} x2={px00.x} y2={px00.y} stroke={strokeColor} strokeWidth={1} strokeDasharray="2,2" opacity={0.5} />
        <line x1={p000.x} y1={p000.y} x2={p0y0.x} y2={p0y0.y} stroke={strokeColor} strokeWidth={1} strokeDasharray="2,2" opacity={0.5} />
        <line x1={p000.x} y1={p000.y} x2={p00z.x} y2={p00z.y} stroke={strokeColor} strokeWidth={1} strokeDasharray="2,2" opacity={0.5} />
        
        {/* Visible Faces with shading */}
        {/* Top face */}
        <polygon
          points={`${p00z.x},${p00z.y} ${px0z.x},${px0z.y} ${pxyz.x},${pxyz.y} ${p0yz.x},${p0yz.y}`}
          fill={color}
          opacity={0.4}
        />
        {/* Left face */}
        <polygon
          points={`${p0y0.x},${p0y0.y} ${pxy0.x},${pxy0.y} ${pxyz.x},${pxyz.y} ${p0yz.x},${p0yz.y}`}
          fill={color}
          opacity={0.7}
        />
        {/* Right face */}
        <polygon
          points={`${px00.x},${px00.y} ${pxy0.x},${pxy0.y} ${pxyz.x},${pxyz.y} ${px0z.x},${px0z.y}`}
          fill={color}
          opacity={0.9}
        />

        {/* Visible Edges */}
        {/* Outer silhouette */}
        <polygon
          points={`${p0yz.x},${p0yz.y} ${p0y0.x},${p0y0.y} ${pxy0.x},${pxy0.y} ${px00.x},${px00.y} ${px0z.x},${px0z.y} ${p00z.x},${p00z.y}`}
          fill="none"
          stroke={strokeColor}
          strokeWidth={1.5}
          strokeLinejoin="round"
        />
        {/* Inner Y-edges */}
        <line x1={pxyz.x} y1={pxyz.y} x2={pxy0.x} y2={pxy0.y} stroke={strokeColor} strokeWidth={1.5} strokeLinejoin="round" />
        <line x1={pxyz.x} y1={pxyz.y} x2={p0yz.x} y2={p0yz.y} stroke={strokeColor} strokeWidth={1.5} strokeLinejoin="round" />
        <line x1={pxyz.x} y1={pxyz.y} x2={px0z.x} y2={px0z.y} stroke={strokeColor} strokeWidth={1.5} strokeLinejoin="round" />
        
        {/* Corner points */}
        <circle cx={p0yz.x} cy={p0yz.y} r={1.2} fill={strokeColor} />
        <circle cx={p0y0.x} cy={p0y0.y} r={1.2} fill={strokeColor} />
        <circle cx={pxy0.x} cy={pxy0.y} r={1.2} fill={strokeColor} />
        <circle cx={px00.x} cy={px00.y} r={1.2} fill={strokeColor} />
        <circle cx={px0z.x} cy={px0z.y} r={1.2} fill={strokeColor} />
        <circle cx={p00z.x} cy={p00z.y} r={1.2} fill={strokeColor} />
        <circle cx={pxyz.x} cy={pxyz.y} r={1.2} fill={strokeColor} />
        
        {/* Dimension labels */}
        {/* h (height) - aligned left of the left vertical edge */}
        <text
          x={p0y0.x - 4}
          y={(p0y0.y + p0yz.y) / 2 + 1.5}
          fontSize={3.5}
          fill="#374151"
          textAnchor="end"
          fontWeight={600}
        >
          h = {height} {unit}
        </text>
        
        {/* w (width) - aligned below the left-bottom edge */}
        <text
          x={(p0y0.x + pxy0.x) / 2 - 4}
          y={(p0y0.y + pxy0.y) / 2 + 6}
          fontSize={3.5}
          fill="#374151"
          textAnchor="end"
          fontWeight={600}
        >
          w = {width} {unit}
        </text>
        
        {/* l (length) - aligned below the right-bottom edge */}
        <text
          x={(px00.x + pxy0.x) / 2 + 4}
          y={(px00.y + pxy0.y) / 2 + 6}
          fontSize={3.5}
          fill="#374151"
          textAnchor="start"
          fontWeight={600}
        >
          l = {length} {unit}
        </text>
      </svg>
    </Box>
  );
};

export default RectangularPrism;
